import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { compareAndTrack, initializeHistory, addHistoryEntry, ChangeType } from '../utils/UserHistoryManager.js';
import { migrateAllUsers, isMigrationCompleted } from '../utils/migrateExistingUsers.js';
import { getRoleName } from '../utils/roleMap.js';
import { logViewAsUserStart, logViewAsUserEnd } from '../utils/ViewAsUserManager.js';
import { generateMockIP } from '../utils/ipUtils.js';
import { createFraudAlert } from '../utils/antiFraudUtils.js';
import { safeParseArray, safeParseObject } from '../utils/storageUtils.js';

// Helper to sanitize user object before saving to localStorage (Privacy Protection)
const sanitizeUserForStorage = (user) => {
  if (!user) return null;

  // Create a safe copy
  const cleanUser = { ...user };

  if (cleanUser.staffDetails) {
    // Destructure to separate sensitive data from the rest
    // Removing: personalData (CPF, Phone, Email), businessData (CNPJ, Address), bankingData (Bank info)
    const { personalData, businessData, bankingData, ...restStaffDetails } = cleanUser.staffDetails;

    // Assign the sanitized version back
    cleanUser.staffDetails = restStaffDetails;
  }

  return cleanUser;
};

// Helper para fetch com timeout (evita travamentos infinitos)
const fetchWithTimeout = async (url, options, timeout = 5000) => {
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(id);
    return response;
  } catch (error) {
    clearTimeout(id);
    throw error;
  }
};

// Contexto de autenticação para gerenciar estado do usuário logado
const AuthContext = createContext();

// Hook personalizado para usar o contexto de autenticação
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth deve ser usado dentro de um AuthProvider');
  }
  return context;
};

// Provider de autenticação
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginStatus, setLoginStatus] = useState(''); // Estado para mensagens de status do login
  const [customRoleMap, setCustomRoleMap] = useState({}); // Mapa de cargos dinâmico
  const [allUsers, setAllUsers] = useState([]); // Todos os usuários do sistema (Array para compatibilidade com o Dashboard)

  // ========== VIEW AS USER STATE ==========
  const [viewAsUser, setViewAsUser] = useState(null); // Usuário sendo simulado
  const [originalUser, setOriginalUser] = useState(null); // Staff original (quem está visualizando)
  const [isViewingAsUser, setIsViewingAsUser] = useState(false); // Flag de modo ativo
  const [currentViewLogId, setCurrentViewLogId] = useState(null); // ID do log atual

  // Helper to normalize Supabase Snake_Case to App CamelCase
  const normalizeUserData = (dbUser) => {
    if (!dbUser) return null;
    return {
      ...dbUser,
      id: dbUser.id || dbUser.discord_id, // Ensure ID mismatch doesn't break
      discordId: dbUser.discord_id || dbUser.discordId,
      accessLevel: dbUser.access_level || dbUser.accessLevel || 'user',
      registrationStatus: dbUser.registration_status || dbUser.registrationStatus,
      isBooster: dbUser.is_booster ?? dbUser.isBooster,
      isStaff: dbUser.is_staff ?? dbUser.isStaff,
      staffDetails: dbUser.staff_details || dbUser.staffDetails,
      frozenData: dbUser.frozen_data || dbUser.frozenData,
      joinDate: dbUser.join_date || dbUser.joinDate,
      dataVersion: dbUser.data_version || dbUser.dataVersion,
      createdAt: dbUser.created_at || dbUser.createdAt,
      updatedAt: dbUser.updated_at || dbUser.updatedAt,
      lastLoginAt: dbUser.last_login || dbUser.lastLoginAt,
      readNotificationIds: dbUser.read_notification_ids || dbUser.readNotificationIds || [],
      notificationsEnabled: dbUser.notifications_enabled ?? dbUser.notificationsEnabled ?? true,
      notificationSounds: dbUser.notification_sounds ?? dbUser.notificationSounds ?? true,
      showAnnouncements: dbUser.show_announcements ?? dbUser.showAnnouncements ?? true,
      // Keep other fields (name, email, phone, avatar, language, cities, terms, signature)
    };
  };
  // ========== STAFF REGISTRATION STATE ==========
  const [preRegisteredStaff, setPreRegisteredStaff] = useState({});
  const [staffRegistrations, setStaffRegistrations] = useState({});
  // ==============================================

  // Helper to persist users (now saving to Firestore for creating new users)
  // This function is kept for compatibility but will write to Firestore
  const saveUsers = async (users) => {
    // In Firestore model, we save individual users, not the whole object at once usually.
    // However, to minimize refactoring impact, we will use this helper to update specific users if passed
    // For now we will rely on individual setDoc calls in the logic below
  };

  // Carregar mapa de cargos salvo
  useEffect(() => {
    const savedMap = safeParseObject('customRoleMap');
    if (Object.keys(savedMap).length > 0) {
      setCustomRoleMap(savedMap);
    }
  }, []);

  const refreshAllUsers = async () => {
    console.log('[Auth] Manual refresh of all users triggered...');
    const { data, error } = await supabase.from('users').select('*');
    if (error) {
      console.error("[Supabase] Error refreshing users:", error);
      throw error;
    }
    const normalized = (data || []).map(normalizeUserData);
    setAllUsers(normalized);
    return normalized;
  };

  // Helper for Session Validation
  const SESSION_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours (Revertido a pedido)

  const isSessionValid = (userData) => {
    if (!userData || !userData.lastLoginAt) return false;
    const lastLoginTime = new Date(userData.lastLoginAt).getTime();
    const now = Date.now();
    return (now - lastLoginTime) < SESSION_DURATION_MS;
  };

  // Periodic Session Check (Every 5 minutes)
  useEffect(() => {
    const interval = setInterval(async () => {
      const storedUser = safeParseObject('user', null);

      // Se não tem usuário, não faz nada
      if (!storedUser) return;

      // Heartbeat: ALWAYS update `last_login` every 5 mins to keep "Staff Online" accurate
      try {
        const now = new Date().toISOString();
        await supabase.from('users').update({ last_login: now }).eq('id', storedUser.id || storedUser.discordId);

        // Also update local storage silently
        const updatedUser = { ...storedUser, lastLoginAt: now };
        localStorage.setItem('user', JSON.stringify(sanitizeUserForStorage(updatedUser)));
        setUser(prev => prev ? { ...prev, lastLoginAt: now } : prev);
      } catch (err) {
        console.error('[Auth] Error updating heartbeat:', err);
      }

      // Se a sessão expirou localmente...
      if (!isSessionValid(storedUser)) {
        console.warn('[Auth] Session active check warning. Attempting silent refresh...');

        // Tenta revalidar com o banco antes de deslogar
        try {
          const { data: dbUser, error } = await supabase
            .from('users')
            .select('id, last_login')
            .eq('id', storedUser.id || storedUser.discordId)
            .single();

          if (!dbUser || error) {
            console.error('[Auth] Silent refresh failed (User not found). Logging out...');
            logout();
            window.location.href = '/login';
          }
        } catch (err) {
          console.error('[Auth] Error during silent refresh:', err);
          // Network error? Don't kill session immediately, wait for next check.
        }
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
    return () => clearInterval(interval);
  }, []);

  // Check Auth & Enable Real-time listener
  useEffect(() => {
    const initAuth = async () => {
      // 1. Check for secure session cookie first (CookieHttpOnly bridge)
      let currentUser = null;
      try {
        const sessionRes = await fetch('/api/auth/session?action=get');
        if (sessionRes.ok) {
          const { session } = await sessionRes.json();
          currentUser = session;
          console.log('[Auth] Restored session from secure cookie.');
        }
      } catch (e) {
        console.debug('[Auth] Cookie session not found, falling back to storage.');
      }

      // 2. Fallback to local storage if cookie session failed
      if (!currentUser) {
        currentUser = safeParseObject('user', null);
      }

      if (!currentUser) {
        setLoading(false);
        return;
      }

      // Re-validate session with Database (CRITICAL: Prevents spoofing)
      try {
        const { data: dbUser, error } = await supabase
          .from('users')
          .select('*')
          .eq('id', currentUser.discordId || currentUser.id)
          .single();

        if (error || !dbUser) {
          console.warn('[Auth] Identity spoofing detected or user not found. Logging out...');
          logout();
          setLoading(false);
          return;
        }

        // [NEW] SESSION HEARTBEAT: Update last_login on every init (Refresh)
        // This ensures that if the user visits the page, their session is renewed.
        const now = new Date().toISOString();
        await supabase.from('users').update({ last_login: now }).eq('id', dbUser.id);
        dbUser.last_login = now; // Update local reference

        const validatedUser = normalizeUserData(dbUser);

        // Update state and local storage with VERIFIED (and updated) data
        // This ensures that even if user modified localStorage, we override it with DB truth.
        setUser(validatedUser);
        localStorage.setItem('user', JSON.stringify(sanitizeUserForStorage(validatedUser)));

        // 2. Conditional data fetching based on VERIFIED access level
        if (validatedUser.accessLevel !== 'user') {
          console.log('[Auth] Ativando sincronização de usuários (Staff Mode)...');

          // Initial fetch
          const { data: usersData } = await supabase.from('users').select('*');
          setAllUsers((usersData || []).map(normalizeUserData));

          // Load Pre-Regs and Staff Regs (Only for Staff)
          const loadSensitiveData = async () => {
            const { data: preRegs } = await supabase.from('pre_registrations').select('*');
            const preRegsMap = {};
            preRegs?.forEach(item => { if (item.data) preRegsMap[item.id] = item.data; });
            setPreRegisteredStaff(preRegsMap);

            const { data: staffRegs } = await supabase.from('staff_registrations').select('*');
            const staffRegsMap = {};
            staffRegs?.forEach(item => { staffRegsMap[item.id] = item.data; });
            setStaffRegistrations(staffRegsMap);
          };
          loadSensitiveData();
        }
      } catch (err) {
        console.error('[Auth] Error during auth init:', err);
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // SAFETY FALLBACK
    const safetyTimeout = setTimeout(() => {
      setLoading((prevLoading) => {
        if (prevLoading) {
          console.warn("[Auth] Supabase took too long. Disabling loader.");
          return false;
        }
        return prevLoading;
      });
    }, 8000);

    return () => {
      clearTimeout(safetyTimeout);
    };
  }, []);

  /* 
   * LEGACY CODE REMOVED: 
   * - Automatic migration (onSnapshot replaces it)
   * - Manual localStorage loading for 'allUsers'
  */



  // Função para fazer login
  const login = async (discordId, password) => {
    setLoading(true);

    try {
      // Simula chamada de API para autenticação
      // Em produção, isso seria uma chamada real para o backend
      const response = await simulateLogin(discordId, password);

      if (response.success) {
        const userData = {
          ...response.user,
          lastLoginAt: new Date().toISOString()
        };

        // Persist activity to global users list
        const savedUsers = getSavedUsers();
        savedUsers[discordId] = { ...(savedUsers[discordId] || {}), ...userData };
        localStorage.setItem('registeredUsers', JSON.stringify(savedUsers));

        setAllUsers(calculateAllUsers());
        setUser(userData);
        localStorage.setItem('user', JSON.stringify(sanitizeUserForStorage(userData)));

        // Sync with secure session API (Cookie)
        try {
          await fetch('/api/auth/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'set', userData: sanitizeUserForStorage(userData) })
          });
        } catch (e) {
          console.warn('[Auth] Failed to set secure session cookie:', e);
        }

        return { success: true, user: userData };
      } else {
        return { success: false, error: response.error };
      }
    } catch (error) {
      console.error('Erro no login:', error);
      return { success: false, error: 'Erro interno do servidor' };
    } finally {
      setLoading(false);
    }
  };

  // Função para fazer login com Discord
  const loginWithDiscord = async (accessToken) => {
    setLoading(true);
    setLoginStatus('Iniciando conexão segura com Discord...');
    try {
      // 1. Obter dados do usuário do Discord
      const discordResponse = await fetchWithTimeout('/api/discord/users/@me', {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });

      if (!discordResponse.ok) {
        throw new Error('Falha ao obter dados do Discord (Token inválido ou expirado)');
      }

      const discordUser = await discordResponse.json();

      // --- OTIMIZAÇÃO: Verificar se usuário já existe ANTES de varrer servidores ---
      let userExists = { exists: false };
      try {
        userExists = await checkUserExists(discordUser.id);
      } catch (err) {
        console.warn('[Auth] Check exists failed, assuming new user.', err);
      }

      if (userExists.exists) {
        setLoginStatus('Usuário encontrado! Sincronizando dados...');
        // Use data from Firestore check instead of legacy getter
        const user = normalizeUserData(userExists.user);

        // --- STAFF PRE-REGISTRATION CHECK FOR EXISTING USERS ---
        const stringId = discordUser.id.toString();

        // 🚀 OTIMIZAÇÃO: Consulta direta ao banco de dados para evitar Race Conditions do estado global
        // Estado global (preRegisteredStaff) pode estar vazio devido a restrições de RLS no fetch inicial massivo.
        // Consultando individualmente, burlamos a necessidade de listar tudo e focamos no usuário atual.
        const { data: directPreStaff } = await supabase
          .from('pre_registrations')
          .select('data')
          .eq('id', stringId)
          .single();

        const { data: directStaffReg } = await supabase
          .from('staff_registrations')
          .select('data')
          .eq('id', stringId)
          .single();

        const preStaffData = directPreStaff?.data;
        const isPending = !!directStaffReg;

        // If pre-registration exists and they haven't submitted the form yet (not pending)
        // BYPASS: Admin ID ignora qualquer pré-registro pendente
        // FIX: Se o usuário JÁ É staff (admin, moderador, etc) OU já tem staffDetails, ignora essa verificação
        if (preStaffData && !isPending && user.accessLevel === 'user' && !user.staffDetails && discordUser.id !== '1217325550771765291') {
          console.log(`[Auth] Existing user ${stringId} found in pre-registration. Redirecting to staff register...`);
          return {
            success: false,
            requiresStaffRegister: true,
            discordData: user,
            preStaffData: preStaffData
          };
        }
        // -------------------------------------------------------

        // ========== FROZEN DATA PROTECTION ==========
        // Frozen data NEVER changes after first login
        // Only dynamic fields are updated and tracked in history

        // Prepare new data from Discord
        const newDiscordData = {
          email: discordUser.email,
          avatar: discordUser.avatar,
          name: discordUser.global_name || discordUser.username
        };

        // Compare and track changes (creates history entries)
        const changes = compareAndTrack(discordUser.id, user, newDiscordData);

        if (changes.length > 0) {
          console.log(`[Auth] Detected ${changes.length} changes for user ${discordUser.id}`);
        }

        // Update ONLY dynamic fields (never touch frozenData)
        // FORCED ADMIN BYPASS: ID 1217325550771765291 is always admin
        const updatedUserData = {
          ...user,
          email: newDiscordData.email,
          avatar: newDiscordData.avatar,
          // name: newDiscordData.name, // REMOVE: Keep the name from the database (user preference)
          lastLoginAt: new Date().toISOString(), // Track last login
          accessLevel: discordUser.id === '1217325550771765291' ? 'admin' : user.accessLevel
        };

        // Save updated data
        updateUser(updatedUserData);
        setUser(updatedUserData);
        localStorage.setItem('user', JSON.stringify(sanitizeUserForStorage(updatedUserData)));

        // Sync with secure session API (Cookie)
        try {
          await fetch('/api/auth/session', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: 'set', userData: sanitizeUserForStorage(updatedUserData) })
          });
        } catch (e) {
          console.warn('[Auth] Failed to update secure session cookie:', e);
        }

        // ... existing punishment update logic ...

        return { success: true, user: updatedUserData };
      }
      // -----------------------------------------------------------------------

      // Se não existe, continua com o processo completo de varredura (Scanner)

      // 2. Configuração dos Servidores (Guilds)
      const GUILDS = {
        '1004174613670023238': 'CAPITAL',
        '1273010402095267840': 'VALLEY NORTE',
        '1341174349469843476': 'MORADA DO VALLEY',
        '1299460857926914141': 'VALLEY SUL'
      };

      const extractedCities = [];
      let highestRole = 'user'; // Nível de acesso base
      let joinDate = null; // Data de entrada mais antiga

      // 3. Iterar sobre os servidores em paralelo (Mais rápido!)
      const totalGuilds = Object.keys(GUILDS).length;
      setLoginStatus(`Iniciando escaneamento de segurança em ${totalGuilds} servidores...`);

      await Promise.all(Object.entries(GUILDS).map(async ([guildId, cityName]) => {
        try {
          // 3.1 Tentar buscar a lista de cargos do servidor
          let roleMap = {};
          try {
            const rolesResponse = await fetchWithTimeout(`/api/discord/guilds/${guildId}/roles`, {
              headers: { Authorization: `Bearer ${accessToken}` }
            }, 3000); // 3s timeout for roles

            if (rolesResponse.ok) {
              const rolesList = await rolesResponse.json();
              rolesList.forEach(role => { roleMap[role.id] = role.name; });
            }
          } catch (error) {
            console.debug(`[Auth] Falha ao buscar cargos de ${cityName}`);
          }

          // 3.2 Buscar dados do membro
          const memberResponse = await fetchWithTimeout(`/api/discord/users/@me/guilds/${guildId}/member`, {
            headers: { Authorization: `Bearer ${accessToken}` }
          }, 3000); // 3s timeout for member data

          if (memberResponse.ok) {
            const memberData = await memberResponse.json();

            // Extrair Nick e ID do Nickname
            const nickname = memberData.nick || discordUser.global_name || discordUser.username;
            let characterName = nickname;
            let characterId = '';

            if (nickname.includes('#')) {
              const parts = nickname.split('#');
              characterName = parts[0].trim();
              const potentialId = parts[1].trim();
              const idMatch = potentialId.match(/^(\d+)/);
              characterId = idMatch ? idMatch[1] : potentialId;
            } else {
              const idMatch = nickname.match(/(\d{3,})/);
              if (idMatch) {
                characterId = idMatch[0];
                characterName = nickname.replace(characterId, '').replace(/[|()\[\]{}-]/g, '').trim();
              }
            }

            const roleNames = memberData.roles.map(roleId => {
              if (customRoleMap[roleId]) return customRoleMap[roleId];
              const manualName = getRoleName(roleId);
              if (manualName !== roleId) return manualName;
              return roleMap[roleId] || roleId;
            });

            // Adiciona à lista de cidades (thread-safe push)
            extractedCities.push({
              city: cityName,
              playerId: characterId || 'PENDENTE',
              characterName: characterName || nickname,
              roles: roleNames,
              joinedAt: memberData.joined_at,
              premiumSince: memberData.premium_since
            });

            if (memberData.joined_at) {
              const memberJoinDate = new Date(memberData.joined_at);
              if (!joinDate || memberJoinDate < new Date(joinDate)) {
                joinDate = memberData.joined_at;
              }
            }
          }
        } catch (error) {
          console.debug(`[Auth] Servidor ${cityName} inacessível.`);
        }
      }));

      setLoginStatus('Varredura completa. Finalizando acesso...');

      // 5. Se chegou aqui, é um usuário novo
      // Prepara objeto de "Rascunho"
      const draftUser = {
        discordId: discordUser.id,
        name: discordUser.global_name || discordUser.username,
        email: discordUser.email,
        avatar: discordUser.avatar,
        cities: extractedCities,
        joinDate: joinDate || new Date().toISOString(),
        isBooster: extractedCities.some(c => c.premiumSince !== null)
      };

      // --- STAFF PRE-REGISTRATION CHECK ---
      const stringId = discordUser.id.toString();

      // 🚀 Consulta direta para novos usuários
      const { data: directPreStaff } = await supabase
        .from('pre_registrations')
        .select('data')
        .eq('id', stringId)
        .single();

      const preStaffData = directPreStaff?.data;

      // BYPASS: Admin ID ignora qualquer pré-registro pendente
      if (preStaffData && !userExists.exists && discordUser.id !== '1217325550771765291') {
        return {
          success: false,
          requiresStaffRegister: true,
          discordData: draftUser,
          preStaffData: preStaffData
        };
      }
      // ------------------------------------

      // ========== BYPASS: Allow specific ID to skip registration ==========
      if (discordUser.id === '1217325550771765291') {
        console.log('[Auth] Bypassing registration for ID 1217325550771765291');
        const bypassUser = {
          ...draftUser,
          accessLevel: 'admin',
          registrationStatus: 'registered',
          createdAt: new Date().toISOString(),
          frozenData: {}
        };

        // Use the new Firestore register function to ensure sync
        return await register(bypassUser);
      }
      // ====================================================================

      // Update pending punishments for this user if they were previously unknown
      try {
        const punishments = safeParseArray('punishments');
        if (punishments.length > 0) {
          let updatedPunishments = [...punishments];
          let hasUpdates = false;

          updatedPunishments = updatedPunishments.map(p => {
            // Check if the punishment is for this user and the characterName is 'Usuário Desconhecido' or empty
            if (p.userId === discordUser.id && (p.characterName === 'Usuário Desconhecido' || !p.characterName)) {
              hasUpdates = true;
              // Link to the first character found in the cities array, otherwise just update the name
              const firstCity = extractedCities[0]; // Use extractedCities from the current scan
              return {
                ...p,
                characterName: firstCity ? firstCity.characterName : discordUser.global_name || discordUser.username,
                characterId: firstCity ? firstCity.playerId : null,
                updatedAt: new Date().toISOString()
              };
            }
            return p;
          });

          if (hasUpdates) {
            localStorage.setItem('punishments', JSON.stringify(punishments));
            console.log(`[Auth] Updated pending punishments for user ${discordUser.id}`);
          }
        }
      } catch (error) {
        console.error('Error updating pending punishments:', error);
      }

      // ========== ANTI-FRAUD: Check for linked profiles ==========
      try {
        const allPunishments = safeParseArray('punishments');
        if (allPunishments.length > 0) {

          // Get mock IP for this user (in production, this would come from the server)
          const userIP = generateMockIP();

          // Build user data for fraud check
          const newUserData = {
            discordId: discordUser.id,
            name: discordUser.global_name || discordUser.username,
            email: discordUser.email,
            ip: userIP
          };

          // Check for fraud matches
          const ticketBans = allPunishments.filter(p =>
            p.punishmentType === 'Blacklist de Tickets' &&
            p.status === 'active' &&
            p.userId !== discordUser.id
          );

          const fraudMatches = [];
          for (const ban of ticketBans) {
            const captured = ban.capturedData || {};
            const matchReasons = [];

            // Check IP match
            if (userIP && captured.ip && userIP === captured.ip) {
              matchReasons.push('IP');
            }

            // Check email match
            if (discordUser.email && captured.email &&
              discordUser.email.toLowerCase() === captured.email.toLowerCase()) {
              matchReasons.push('Email');
            }

            if (matchReasons.length > 0) {
              fraudMatches.push({
                punishment: ban,
                matchReasons,
                bannedUserId: ban.userId,
                bannedUserName: ban.characterName || 'Usuário Desconhecido'
              });
            }
          }

          // If fraud detected, create alert ticket and notification
          if (fraudMatches.length > 0) {
            console.log(`[Anti-Fraud] 🚨 Detected ${fraudMatches.length} fraud match(es) for new user ${discordUser.id}`);
            await createFraudAlert(newUserData, fraudMatches);
          }
        }
      } catch (error) {
        console.error('[Anti-Fraud] Error checking for fraud:', error);
      }
      // ==========================================================

      return {
        success: false,
        requiresRegister: true,
        discordData: draftUser
      };

    } catch (error) {
      console.error('Erro no login com Discord:', error);
      return { success: false, error: error.message || 'Erro ao autenticar. Tente novamente.' };
    } finally {
      setLoading(false);
    }
  };

  // Função para fazer logout
  const logout = async () => {
    setUser(null);
    setAllUsers([]); // Clear optimized data
    localStorage.removeItem('user');

    // Clear secure session cookie
    try {
      await fetch('/api/auth/session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'clear' })
      });
    } catch (e) {
      console.warn('[Auth] Failed to clear secure session cookie:', e);
    }
  };

  // Função para verificar se usuário existe
  const checkUserExists = async (discordId) => {
    try {
      if (!discordId) return { exists: false };
      const stringId = discordId.toString();

      // 1. PRIORIDADE: Verificar no estado sincronizado (allUsers)
      const existingUser = Array.isArray(allUsers)
        ? allUsers.find(u => u.discordId === stringId || u.id === stringId)
        : null;

      if (existingUser) {
        return { exists: true, user: existingUser };
      }

      // 2. Busca direta no Supabase
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', stringId)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 is "no rows returned"
        throw error;
      }

      if (data) {
        return { exists: true, user: normalizeUserData(data) };
      }

      return { exists: false };
    } catch (error) {
      console.error('[Auth] Erro ao verificar usuário:', error);
      return { exists: false, error: 'Erro ao conectar com o banco de dados' };
    }
  };

  // Função para registrar novo usuário
  const register = async (userData) => {
    try {
      // 1. PROCESSAR CIDADES (Filters & Status)
      const ticketsToCreate = [];
      const processedCities = (userData.cities || [])
        .filter(city => city.playerId !== 'SEM_PERSONAGEM') // Remove "Não tenho personagem"
        .map(city => {
          if (city.isManual) {
            // Manual entry: Mark as pending and queue ticket
            ticketsToCreate.push(city);
            return { ...city, status: 'pending_approval' };
          }
          // Automatic entry: Verified
          return { ...city, status: 'verified' };
        });

      // Prepare payload for Supabase (Normalized Snake Case)
      const userPayload = {
        id: userData.discordId.toString(),
        discord_id: userData.discordId.toString(),
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
        avatar: userData.avatar,
        language: userData.language || 'pt-BR',
        cities: processedCities, // Updated list
        access_level: 'user',
        join_date: userData.joinDate,
        is_booster: userData.isBooster || false,
        registration_status: 'registered',
        frozen_data: userData.frozenData,
        data_version: userData.dataVersion,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        last_login: new Date().toISOString()
      };

      // State can have camelCase for app usage
      const newUser = normalizeUserData({
        ...userData,
        ...userPayload,
        discord_id: userData.discordId.toString(),
        access_level: 'user',
        is_booster: userData.isBooster,
        registration_status: 'registered',
        cities: processedCities
      });

      // Otimista
      setUser(newUser);
      localStorage.setItem('user', JSON.stringify(sanitizeUserForStorage(newUser)));

      // 2. SALVAR USUÁRIO PRIMEIRO
      const { error } = await supabase.from('users').upsert(userPayload);

      if (error) throw error;

      // 3. CRIAR TICKETS PARA CIDADES MANUAIS
      if (ticketsToCreate.length > 0) {
        console.log(`[Auth] Generating ${ticketsToCreate.length} verification tickets...`);

        const timestamp = new Date().toISOString();

        const ticketPromises = ticketsToCreate.map(city => {
          const shortId = Math.random().toString(36).substr(2, 9).toUpperCase();
          const ticketPayload = {
            id: shortId,
            custom_id: shortId,
            display_id: shortId,
            title: `Validação de Vínculo: ${city.city}`,
            category: 'suporte',
            status: 'aguardando_sistema',
            auxiliary_status: [],
            priority: 'high',
            department: 'suporte',
            user_id: userData.discordId.toString(),
            user_discord_id: userData.discordId.toString(),
            user_name: userData.name,
            user_avatar: userData.avatar,
            city: city.city,
            description: `Solicitação de vínculo manual para ${city.city}.\n\nO usuário informou manualmente os dados abaixo pois o scanner não detectou automaticamente.\n\n**Personagem:** ${city.characterName}\n**ID Informado:** ${city.playerId}\n**Discord:** ${userData.name}\n**ID do Usuário:** ${userData.discordId}`,
            type: 'manual_verification',
            metadata: {
              verification_type: 'manual_city',
              city_data: city,
              original_scanner_data: city // Contains what scanner found (likely PENDENTE)
            },
            player_id: city.playerId,
            character_name: city.characterName,
            created_at: timestamp,
            last_update: timestamp,
            logs: [{
              id: Date.now(),
              type: 'creation',
              message: 'Ticket de validação gerado automaticamente',
              userId: 'system',
              userName: 'System',
              userRole: 'system',
              timestamp: timestamp
            }]
          };
          return supabase.from('tickets').insert(ticketPayload);
        });

        await Promise.all(ticketPromises);
        console.log('[Auth] Tickets created successfully.');
      }

      console.log(`[Auth] Usuário ${newUser.discordId} registrado e sincronizado com sucesso.`);

      const successMessage = ticketsToCreate.length > 0
        ? 'Cadastro realizado! Tickets de validação foram criados para suas cidades manuais.'
        : 'Usuário registrado com sucesso!';

      return { success: true, message: successMessage, user: newUser };
    } catch (error) {
      console.error('Erro crítica no registro:', error);
      // Rollback optimistic update
      setUser(null);
      localStorage.removeItem('user');
      return { success: false, error: 'Erro ao salvar dados na nuvem.' };
    }
  };

  // Função para atualizar dados do usuário
  const updateUser = async (updatedData) => {
    if (!user) return { success: false, error: 'Usuário não logado.' };

    const mergedUser = { ...user, ...updatedData, updatedAt: new Date().toISOString() };
    setUser(mergedUser);
    localStorage.setItem('user', JSON.stringify(sanitizeUserForStorage(mergedUser)));

    // Map updates to snake_case
    const dbUpdate = {};
    if (updatedData.name) dbUpdate.name = updatedData.name;
    if (updatedData.email) dbUpdate.email = updatedData.email;
    if (updatedData.avatar) dbUpdate.avatar = updatedData.avatar;
    if (updatedData.phone) dbUpdate.phone = updatedData.phone;
    if (updatedData.lastLoginAt) dbUpdate.last_login = updatedData.lastLoginAt;
    if (updatedData.accessLevel) dbUpdate.access_level = updatedData.accessLevel;
    if (updatedData.staffDetails) dbUpdate.staff_details = updatedData.staffDetails; // FIX: Ensure staffDetails are persisted
    if (updatedData.readNotificationIds !== undefined) dbUpdate.read_notification_ids = updatedData.readNotificationIds;
    if (updatedData.notificationsEnabled !== undefined) dbUpdate.notifications_enabled = updatedData.notificationsEnabled;
    if (updatedData.notificationSounds !== undefined) dbUpdate.notification_sounds = updatedData.notificationSounds;
    if (updatedData.showAnnouncements !== undefined) dbUpdate.show_announcements = updatedData.showAnnouncements;

    dbUpdate.updated_at = new Date().toISOString();

    try {
      const { error } = await supabase
        .from('users')
        .update(dbUpdate)
        .eq('id', user.discordId);

      if (error) throw error;
      return { success: true, user: mergedUser };
    } catch (error) {
      console.error('Erro ao atualizar usuário:', error);
      return { success: false, error: 'Falha ao sincronizar atualização com a nuvem.' };
    }
  };

  // Função para criar um pré-perfil para usuários não registrados
  const createPreProfile = async (serverName, discordId, characterId) => {
    try {
      const stringId = discordId.toString();

      const { data: existing } = await supabase.from('users').select('id').eq('id', stringId).single();
      if (existing) return { success: false, error: 'Usuário já possui registro.' };

      const preProfile = {
        id: stringId,
        discordId,
        name: 'Pré-Registro',
        avatar: null,
        email: null,
        frozenData: {},
        cities: [
          {
            city: serverName,
            playerId: characterId,
            characterName: 'Usuário Desconhecido',
            roles: [],
            joinedAt: new Date().toISOString(),
            premiumSince: null
          }
        ],
        accessLevel: 'user',
        createdAt: new Date().toISOString(),
        isPreProfile: true
      };

      const { error } = await supabase.from('users').insert(preProfile);
      if (error) throw error;

      return { success: true, user: preProfile };
    } catch (error) {
      console.error('Erro ao criar pré-perfil:', error);
      return { success: false, error: 'Erro ao salvar pré-perfil.' };
    }
  };

  // ADMIN ONLY: Function to update frozen data
  const adminUpdateFrozenData = async (targetUserId, field, newValue, adminUser) => {
    try {
      // Verify admin permissions
      if (!adminUser || adminUser.accessLevel !== 'admin') {
        console.error('[Admin] Unauthorized attempt to modify frozen data');
        return {
          success: false,
          error: 'Apenas administradores podem modificar dados congelados'
        };
      }

      // Load target user
      const savedUsers = getSavedUsers();
      const targetUser = savedUsers[targetUserId];

      if (!targetUser) {
        return { success: false, error: 'Usuário não encontrado' };
      }

      if (!targetUser.frozenData) {
        return { success: false, error: 'Usuário não possui dados congelados' };
      }

      // Store old value for history
      const oldValue = targetUser.frozenData[field];

      // Update frozen data
      targetUser.frozenData[field] = newValue;
      targetUser.frozenData.lastModifiedAt = new Date().toISOString();
      targetUser.frozenData.lastModifiedBy = adminUser.discordId;

      // Save to localStorage
      savedUsers[targetUserId] = targetUser;
      localStorage.setItem('registeredUsers', JSON.stringify(savedUsers));

      // Create history entry with admin flag
      addHistoryEntry(
        targetUserId,
        ChangeType.FROZEN_DATA_ADMIN,
        oldValue,
        newValue,
        {
          field,
          isAdminChange: true,
          adminId: adminUser.discordId,
          adminName: adminUser.name,
          reason: 'Admin override of frozen data'
        }
      );

      console.log(`[Admin] ${adminUser.name} modified frozen field '${field}' for user ${targetUserId}`);

      return {
        success: true,
        message: 'Dados congelados atualizados com sucesso',
        oldValue,
        newValue
      };

    } catch (error) {
      return { success: false, error: 'Erro ao atualizar dados congelados' };
    }
  };

  const adminFullUpdateUser = async (targetUserId, updatedData) => {
    try {
      if (!user || user.accessLevel !== 'admin') {
        return { success: false, error: 'Acesso negado' };
      }

      const savedUsers = getSavedUsers();
      let targetUser = savedUsers[targetUserId];

      if (!targetUser) {
        // Fallback 1: Check in-memory allUsers state
        if (Array.isArray(allUsers)) {
          targetUser = allUsers.find(u => u.discordId === targetUserId || u.id === targetUserId);
        } else if (allUsers && typeof allUsers === 'object') {
          targetUser = Object.values(allUsers).find(u => u.discordId === targetUserId || u.id === targetUserId);
        }

        // Fallback 2: Fetch from Supabase
        if (!targetUser) {
          const { data, error } = await supabase.from('users').select('*').eq('id', targetUserId).single();
          if (data && !error) {
            targetUser = normalizeUserData(data);
            // Cache it to avoid future misses?
            // savedUsers[targetUserId] = targetUser; // Optional but good for consistency
          }
        }
      }

      if (!targetUser) {
        return { success: false, error: 'Usuário não encontrado' };
      }

      // Merge updated data (LOCAL STATE - camelCase)
      const updatedUser = {
        ...targetUser,
        ...updatedData,
        updatedAt: new Date().toISOString(),
        updatedBy: user.discordId
      };

      // PREPARE SUPABASE PAYLOAD (snake_case)
      // We map the updatedUser fields to the DB columns
      const dbPayload = {
        id: updatedUser.discordId.toString(),
        discord_id: updatedUser.discordId.toString(), // Ensure string
        name: updatedUser.name,
        email: updatedUser.email,
        phone: updatedUser.phone,
        avatar: updatedUser.avatar,
        access_level: updatedUser.accessLevel,
        registration_status: updatedUser.registrationStatus,
        cities: updatedUser.cities, // JSONB
        frozen_data: updatedUser.frozenData,
        staff_details: updatedUser.staffDetails,
        updated_at: new Date().toISOString()
      };

      // Save to storage (Legacy/Offline support)
      savedUsers[targetUserId] = updatedUser;
      localStorage.setItem('registeredUsers', JSON.stringify(savedUsers));

      // Update local state if needed
      setAllUsers(savedUsers);

      // If updating self, update user state
      if (user.discordId === targetUserId) {
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(sanitizeUserForStorage(updatedUser)));
      }

      // PERSIST TO SUPABASE
      const { error } = await supabase
        .from('users')
        .update(dbPayload)
        .eq('id', targetUserId);

      if (error) {
        console.error('[Supabase] Error updating user:', error);
        throw error;
      }

      return { success: true, user: updatedUser };
    } catch (error) {
      console.error('[Admin] Error performing full user update:', error);
      return { success: false, error: 'Erro ao atualizar dados do usuário: ' + error.message };
    }
  };

  /**
   * Função para excluir completamente um usuário do sistema
   * REMOVE: Dados de registro, histórico, pré-registros, cadastros pendentes
   * NOTA: Tickets devem ser removidos separadamente via TicketContext.deleteAllTicketsFromUser
   */
  const deleteUser = async (targetUserId) => {
    try {
      if (!user || user.accessLevel !== 'admin') {
        return { success: false, error: 'Acesso negado. Apenas administradores podem excluir usuários.' };
      }

      console.log(`[Admin] Initiating DELETION of user ${targetUserId} by admin ${user.discordId}`);

      // 1. Remover de registeredUsers
      const savedUsers = getSavedUsers();
      if (savedUsers[targetUserId]) {
        delete savedUsers[targetUserId];
        localStorage.setItem('registeredUsers', JSON.stringify(savedUsers));
        console.log(`[Admin] Deleted from registeredUsers`);
      }

      // 2. Remover de preRegisteredStaff
      const savedPreStaff = localStorage.getItem('preRegisteredStaff');
      if (savedPreStaff) {
        const preStaffData = JSON.parse(savedPreStaff);
        if (preStaffData[targetUserId]) {
          delete preStaffData[targetUserId];
          localStorage.setItem('preRegisteredStaff', JSON.stringify(preStaffData));
          setPreRegisteredStaff(preStaffData);
          console.log(`[Admin] Deleted from preRegisteredStaff`);
        }
      }

      // 3. Remover de staffRegistrations
      const savedStaffRegs = localStorage.getItem('staffRegistrations');
      if (savedStaffRegs) {
        const staffRegs = JSON.parse(savedStaffRegs);
        if (staffRegs[targetUserId]) {
          delete staffRegs[targetUserId];
          localStorage.setItem('staffRegistrations', JSON.stringify(staffRegs));
          setStaffRegistrations(staffRegs);
          console.log(`[Admin] Deleted from staffRegistrations`);
        }
      }

      // 4. Remover punições (Supabase + LocalStorage)
      try {
        const { error: punError } = await supabase.from('punishments').delete().eq('user_id', idStr);
        if (punError) console.error('[Supabase] Error deleting punishments:', punError);
        else console.log(`[Admin] Deleted punishments for user ${idStr}`);
      } catch (err) {
        console.warn('[Admin] Failed to delete punishments from DB, might create orphans if not cascade.', err);
      }

      // Also clean local storage just in case
      let punishments = safeParseArray('punishments');
      if (punishments.length > 0) {
        punishments = punishments.filter(p => p.userId !== targetUserId);
        localStorage.setItem('punishments', JSON.stringify(punishments));
      }

      // 5. Limpar histórico (via localStorage direto pois UserHistoryManager não tem delete público fácil aqui)
      const historyKey = `userHistory_${targetUserId}`;
      localStorage.removeItem(historyKey);
      console.log(`[Admin] Deleted user history`);

      // 6. Atualizar estado local
      setAllUsers(savedUsers);

      return { success: true };
    } catch (error) {
      console.error('[Admin] Error deleting user:', error);
      return { success: false, error: 'Erro ao excluir usuário: ' + error.message };
    }
  };

  // Função para atualizar o mapeamento de cargos
  const updateRoleMapping = (id, name) => {
    const newMap = { ...customRoleMap, [id]: name };
    setCustomRoleMap(newMap);
    localStorage.setItem('customRoleMap', JSON.stringify(newMap));
    return true;
  };

  // Função para remover mapeamento de cargo
  const removeRoleMapping = (id) => {
    const newMap = { ...customRoleMap };
    delete newMap[id];
    setCustomRoleMap(newMap);
    localStorage.setItem('customRoleMap', JSON.stringify(newMap));
    return true;
  };

  // NOVA ESTRUTURA: Estado para permissões dinâmicas de cargos
  const [rolePermissions, setRolePermissions] = useState({
    // Fallback inicial (Nível Operacional - Especialistas)
    'suporte': ['suporte_duvidas'],
    'suporte_compras': ['compras_doacoes'],
    'suporte_descontos': ['cupons_descontos'],
    'suporte_tecnico': ['programacao'],
    'moderador': ['denuncia_jogador'],
    'super_moderador': ['denuncia_jogador', 'contestar_punicao', 'contestar_banimento'],
    'programador': ['programacao'],
    // Nível Supervisão - Heads
    'head_suporte': ['suporte_duvidas', 'compras_doacoes', 'cupons_descontos', 'programacao'],
    'head_moderacao': ['denuncia_jogador', 'contestar_punicao', 'contestar_banimento'],
    'head_staff': ['suporte_duvidas', 'compras_doacoes', 'cupons_descontos', 'programacao', 'denuncia_jogador', 'contestar_punicao', 'contestar_banimento'],
    // Nível Executivo
    'direcao': ['suporte_duvidas', 'compras_doacoes', 'cupons_descontos', 'programacao', 'denuncia_jogador', 'contestar_punicao', 'contestar_banimento', 'denuncia_staff'],
    'admin': ['suporte_duvidas', 'compras_doacoes', 'cupons_descontos', 'programacao', 'denuncia_jogador', 'contestar_punicao', 'contestar_banimento', 'denuncia_staff'],
    'analista_qualidade': ['suporte_duvidas', 'compras_doacoes', 'cupons_descontos', 'programacao', 'denuncia_jogador', 'contestar_punicao', 'contestar_banimento', 'denuncia_staff']
  });

  // Buscar permissões dinâmicas do banco de dados
  useEffect(() => {
    const fetchRolePermissions = async () => {
      try {
        const { data, error } = await supabase
          .from('system_settings')
          .select('value')
          .eq('key', 'staff_role_permissions')
          .single();

        if (data && data.value) {
          setRolePermissions(data.value);
        }
      } catch (err) {
        console.error('[AuthContext] Erro ao buscar permissões de cargos:', err);
      }
    };

    fetchRolePermissions();
  }, []);

  // API para atualizar permissões de cargos (exclusivo para admins)
  const updateRolePermissions = async (newPermissions) => {
    if (!user || user.accessLevel !== 'admin') return { success: false, error: 'Acesso negado' };

    try {
      // Atualiza banco
      const { error } = await supabase
        .from('system_settings')
        .upsert({
          key: 'staff_role_permissions',
          value: newPermissions,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      // Atualiza estado local
      setRolePermissions(newPermissions);

      return { success: true };
    } catch (err) {
      console.error('[AuthContext] Erro ao salvar permissões:', err);
      return { success: false, error: 'Erro ao salvar permissões no banco' };
    }
  };

  // NOVA ESTRUTURA: Função para verificar acesso a categoria específica
  const hasAccessToCategory = (category) => {
    if (!user) return false;

    // Admin e Direção têm acesso a tudo por padrão (fallback)
    if (['admin', 'direcao'].includes(user.accessLevel)) return true;

    return rolePermissions[user.accessLevel]?.includes(category) || false;
  };

  // NOVA ESTRUTURA: Função para obter categorias acessíveis
  const getAccessibleCategories = () => {
    if (!user) return [];

    return rolePermissions[user.accessLevel] || [];
  };

  // NOVA ESTRUTURA: Função para verificar nível hierárquico
  const getHierarchyLevel = () => {
    if (!user) return 0;

    const hierarchyLevels = {
      'user': 0,
      'suporte': 1,
      'suporte_compras': 1,
      'suporte_descontos': 1,
      'suporte_tecnico': 1,
      'moderador': 1,
      'super_moderador': 1,
      'programador': 1,
      'head_suporte': 2,
      'head_moderacao': 2,
      'head_staff': 3,
      'direcao': 4,
      'admin': 4,
      'analista_qualidade': 3
    };

    return hierarchyLevels[user.accessLevel] || 0;
  };

  // ========== VIEW AS USER FUNCTIONS ==========

  /**
   * Inicia o modo "Visualizar como Usuário"
   * @param {string} targetUserId - Discord ID do usuário a ser simulado
   * @returns {Object} Resultado da operação
   */
  const startViewAsUser = async (targetUserId) => {
    try {
      // Verificar se já está em modo view-as-user
      if (isViewingAsUser) {
        return {
          success: false,
          error: 'Você já está visualizando como outro usuário. Retorne ao seu painel primeiro.'
        };
      }

      // Verificar permissão (apenas Heads e acima)
      if (getHierarchyLevel() < 2) {
        return {
          success: false,
          error: 'Você não tem permissão para usar este recurso.'
        };
      }

      // Não permitir visualizar como si mesmo
      if (user.discordId === targetUserId) {
        return {
          success: false,
          error: 'Você não pode visualizar como você mesmo.'
        };
      }

      // Buscar usuário alvo
      const savedUsers = getSavedUsers();
      let targetUser = savedUsers[targetUserId];

      // Fallback: Check allUsers array logic
      if (!targetUser) {
        if (Array.isArray(allUsers)) {
          targetUser = allUsers.find(u => u.discordId === targetUserId || u.id === targetUserId);
        } else if (allUsers && typeof allUsers === 'object') {
          targetUser = Object.values(allUsers).find(u => u.discordId === targetUserId || u.id === targetUserId);
        }
      }

      // Se não encontrar usuário registrado, verifica se é um pré-cadastro
      if (!targetUser) {
        const preStaff = preRegisteredStaff[targetUserId];

        if (preStaff) {
          // Retorna flag especial para redirecionamento
          console.log(`[ViewAsUser] Usuário ${targetUserId} é pré-cadastro. Redirecionando...`);
          return {
            success: true,
            isPreRegistered: true,
            preStaffData: preStaff,
            discordData: {
              discordId: preStaff.discordId,
              name: preStaff.codename || 'Usuário Desconhecido',
              // Mock minimal data needed for registration page preview
              email: 'email@simulado.com',
              avatar: null
            }
          };
        }

        return {
          success: false,
          error: 'Usuário não encontrado.'
        };
      }

      // Salvar usuário original
      setOriginalUser(user);

      // Definir usuário simulado
      setViewAsUser(targetUser);
      setUser(targetUser);
      setIsViewingAsUser(true);

      // Registrar no log
      const logId = logViewAsUserStart(
        user.discordId,
        user.name,
        targetUser.discordId,
        targetUser.name
      );
      setCurrentViewLogId(logId);

      console.log(`[ViewAsUser] ${user.name} iniciou visualização como ${targetUser.name} `);

      return {
        success: true,
        message: `Agora visualizando como ${targetUser.name} `,
        targetUser
      };
    } catch (error) {
      console.error('[ViewAsUser] Erro ao iniciar visualização:', error);
      return {
        success: false,
        error: 'Erro ao iniciar modo de visualização.'
      };
    }
  };

  /**
   * Encerra o modo "Visualizar como Usuário" e retorna ao painel original
   * @returns {Object} Resultado da operação
   */
  const stopViewAsUser = () => {
    try {
      if (!isViewingAsUser) {
        return {
          success: false,
          error: 'Você não está em modo de visualização.'
        };
      }

      // Finalizar log
      if (currentViewLogId) {
        logViewAsUserEnd(currentViewLogId);
      }

      // Restaurar usuário original
      setUser(originalUser);
      localStorage.setItem('user', JSON.stringify(sanitizeUserForStorage(originalUser)));

      // Limpar estados
      setViewAsUser(null);
      setOriginalUser(null);
      setIsViewingAsUser(false);
      setCurrentViewLogId(null);

      console.log('[ViewAsUser] Modo de visualização encerrado');

      return {
        success: true,
        message: 'Retornado ao seu painel original.'
      };
    } catch (error) {
      console.error('[ViewAsUser] Erro ao encerrar visualização:', error);
      return {
        success: false,
        error: 'Erro ao retornar ao painel original.'
      };
    }
  };
  // Update staff data (for missing information correction)
  const updateStaffData = async (discordId, updatedDetails) => {
    try {
      const savedUsers = getSavedUsers();

      if (!savedUsers[discordId]) {
        return { success: false, error: 'Usuário não encontrado' };
      }

      const updatedUser = {
        ...savedUsers[discordId],
        staffDetails: updatedDetails
      };

      const newSavedUsers = {
        ...savedUsers,
        [discordId]: updatedUser
      };

      saveUsers(newSavedUsers);
      setAllUsers(calculateAllUsers());

      if (user && user.discordId === discordId) {
        const newUserState = { ...user, staffDetails: updatedDetails };
        setUser(newUserState);
        localStorage.setItem('user', JSON.stringify(sanitizeUserForStorage(newUserState)));
      }

      return { success: true };
    } catch (error) {
      console.error('Error updating staff data:', error);
      return { success: false, error: 'Erro ao atualizar dados' };
    }
  };

  const value = {
    user,
    updateStaffData,
    loading,
    loginStatus, // Exporta o status
    login,
    loginWithDiscord,
    logout,
    checkUserExists,
    register,
    updateUser,
    createPreProfile, // Adicionado createPreProfile
    adminUpdateFrozenData, // Admin function to modify frozen data
    isAuthenticated: !!user,

    // Gestão de Cargos
    customRoleMap,
    updateRoleMapping,
    removeRoleMapping,

    // NOVA ESTRUTURA: Verificações de acesso granulares e Permissões Dinâmicas
    rolePermissions,
    updateRolePermissions,
    hasAccessToCategory,
    getAccessibleCategories,
    getHierarchyLevel,

    // Verificações de nível (mantidas para compatibilidade)
    isAdmin: user?.accessLevel === 'admin',
    isHeadStaff: user?.accessLevel === 'head_staff',
    isHeadSupport: user?.accessLevel === 'head_suporte',
    isHeadModeration: user?.accessLevel === 'head_moderacao',
    isProgrammer: user?.accessLevel === 'programador',
    isSupport: ['suporte', 'suporte_compras', 'suporte_descontos'].includes(user?.accessLevel),
    isModerator: ['moderador', 'super_moderador'].includes(user?.accessLevel),

    // Verificações de departamento
    isInSupportDepartment: ['suporte', 'suporte_compras', 'suporte_descontos', 'head_suporte'].includes(user?.accessLevel),
    isInModerationDepartment: ['moderador', 'super_moderador', 'head_moderacao'].includes(user?.accessLevel),
    isInProgrammingDepartment: user?.accessLevel === 'programador',

    // Verificações de supervisão
    isStaff: getHierarchyLevel() >= 1,
    isHead: getHierarchyLevel() >= 2,
    isExecutive: getHierarchyLevel() >= 3,

    // ========== VIEW AS USER ==========
    startViewAsUser,
    isViewingAsUser,
    viewAsUser,
    originalUser,
    stopViewAsUser,
    refreshAllUsers,
    allUsers: Array.isArray(allUsers) ? allUsers : Object.values(allUsers),
    allUsersMap: Array.isArray(allUsers) ? allUsers.reduce((map, u) => ({ ...map, [u.discordId]: u }), {}) : allUsers,
    adminFullUpdateUser,

    // ========== STAFF REGISTRATION ==========
    preRegisteredStaff,
    staffRegistrations,
    // Função para adicionar um pré-registro de staff
    addStaffPreRegistration: async (discordId, accessLevel, contractValue, contractDuration, codename, serviceDescription, contractUnit = 'months') => {
      console.log('[Auth] addStaffPreRegistration called with:', { discordId, accessLevel, contractValue, contractDuration, codename, serviceDescription, contractUnit });

      const idStr = discordId.toString();
      const newPreStaffData = {
        discordId: idStr,
        accessLevel,
        contractValue,
        contractDuration,
        contractUnit,
        codename,
        serviceDescription,
        createdat: new Date().toISOString()
      };

      // OPTIMISTIC UPDATE
      setPreRegisteredStaff(prev => ({
        ...prev,
        [idStr]: newPreStaffData
      }));

      try {
        // 1. Save to Supabase (Real-time sync)
        const preStaffDataPayload = {
          id: idStr,
          data: newPreStaffData
        };

        const { error: preRegError } = await supabase.from('pre_registrations').upsert(preStaffDataPayload);
        if (preRegError) throw preRegError;

        // 2. Propagate to Active User (New Logic)
        const targetUser = allUsers.find(u => u.discordId === idStr || u.id === idStr);

        if (targetUser) {
          console.log('[Auth] Propagating changes to active user:', idStr);

          const updatedStaffDetails = {
            ...targetUser.staffDetails,
            role: accessLevel,
            codename: codename,
            contractData: {
              ...targetUser.staffDetails?.contractData,
              contractValue: contractValue,
              contractDuration: contractDuration,
              contractUnit: contractUnit,
              contractTerm: `${contractDuration} ${contractUnit === 'days' ? 'dias' : 'meses'}`,
              serviceDescription: serviceDescription
            }
          };

          // FIX: Use snake_case for DB columns
          const dbPayload = {
            access_level: accessLevel,
            staff_details: updatedStaffDetails
          };

          const { error: userUpdateError } = await supabase
            .from('users')
            .update(dbPayload)
            .eq('id', idStr);

          if (userUpdateError) throw userUpdateError;

          if (user && user.discordId === idStr) {
            // Update local state with camelCase
            setUser(prev => ({
              ...prev,
              accessLevel: accessLevel,
              staffDetails: updatedStaffDetails
            }));
          }
        }
        return { success: true };
      } catch (err) {
        console.error('[Supabase] Error adding pre-registration:', err);
        // ROLLBACK
        setPreRegisteredStaff(prev => {
          const newState = { ...prev };
          delete newState[idStr];
          return newState;
        });
        return { success: false, error: 'Erro ao salvar pré-registro.' };
      }
    },
    removeStaffPreRegistration: async (discordId) => {
      const idStr = discordId.toString();
      // OPTIMISTIC UPDATE
      const previousState = { ...preRegisteredStaff };
      setPreRegisteredStaff(prev => {
        const newState = { ...prev };
        delete newState[idStr];
        return newState;
      });

      try {
        const { error } = await supabase.from('pre_registrations').delete().eq('id', idStr);
        if (error) throw error;
        return { success: true };
      } catch (error) {
        console.error('[Supabase] Error removing pre-registration:', error);
        // ROLLBACK
        setPreRegisteredStaff(previousState);
        return { success: false, error: 'Erro ao remover pré-registro.' };
      }
    },

    /**
     * Ensures a user record exists for manual punishments (Pre-Registration Flow)
     * If user doesn't exist, creates a minimal record.
     */
    ensurePreRegisteredUser: async (discordId, name, ip = null) => {
      const idStr = discordId.toString();

      // Check local cache first
      if (allUsers.find(u => u.discordId === idStr)) {
        return { success: true, message: 'User already exists' };
      }

      try {
        // Double check database to be sure
        const { data: existing, error: fetchError } = await supabase.from('users').select('id').eq('id', idStr).maybeSingle();
        if (fetchError && fetchError.code !== 'PGRST116') throw fetchError;
        if (existing) return { success: true, message: 'User already exists in DB' };

        const newUserPayload = {
          id: idStr,
          discord_id: idStr,
          name: name || 'Usuário Desconhecido',
          registration_status: 'pre_registered', // Special status
          access_level: 'user',
          created_at: new Date().toISOString(),
          avatar: null,
          email: null,
          frozen_data: ip ? { ip } : {}
        };

        const { error: insertError } = await supabase.from('users').insert(newUserPayload);
        if (insertError) throw insertError;

        // Update local state
        const localUser = normalizeUserData(newUserPayload);
        if (localUser) {
          setAllUsers(prev => [...prev, localUser]);
        }

        return { success: true, user: localUser };
      } catch (error) {
        console.error('[Auth] Error ensuring pre-registered user:', error);
        return { success: false, error };
      }
    },
    // Função para registrar um staff (enviado pelo próprio usuário)
    registerStaff: async (staffData) => {
      setLoading(true);
      try {
        const idStr = (staffData.discordId || staffData.discordData?.discordId || staffData.discordData?.id || '').toString();

        if (!idStr || idStr === 'undefined') {
          throw new Error('ID do Discord não encontrado nos dados de registro.');
        }

        const newRegistration = {
          id: idStr,
          data: {
            ...staffData,
            status: 'pending'
          }
        };

        const { error: regError } = await supabase.from('staff_registrations').upsert(newRegistration);
        if (regError) throw regError;

        const draftStaffUser = {
          id: idStr,
          discordid: idStr,
          name: staffData.personalData.fullName,
          email: staffData.discordData.email,
          avatar: staffData.discordData.avatar,
          accesslevel: staffData.preStaffData.accessLevel,
          registrationstatus: 'pending_approval',
          isstaff: true,
          ...staffData // Still need other data for local state?
        };

        // Strict Payload for Supabase (Snake Case)
        const dbPayload = {
          id: idStr,
          discord_id: idStr,
          name: staffData.personalData.fullName,
          email: staffData.discordData.email,
          phone: staffData.personalData.phone,
          avatar: staffData.discordData.avatar,
          access_level: staffData.preStaffData.accessLevel,
          registration_status: 'pending_approval',
          is_staff: true,
          staff_details: {
            ...staffData,
            submittedAt: new Date().toISOString()
          },
          created_at: new Date().toISOString(),
        };



        const { error: userError } = await supabase.from('users').upsert(dbPayload);
        if (userError) throw userError;

        setUser(draftStaffUser);
        localStorage.setItem('user', JSON.stringify(draftStaffUser));

        return { success: true, user: draftStaffUser };
      } catch (error) {
        console.error('[Supabase] Error registering staff:', error);
        return { success: false, error: 'Erro ao processar cadastro' };
      } finally {
        setLoading(false);
      }
    },

    deleteUser: async (discordId) => {
      try {
        const idStr = discordId.toString();

        // 0. Check if it is a core user and add to blacklist
        const coreUsers = getCoreUsers();
        if (coreUsers[idStr]) {
          const deletedCoreUsers = safeParseArray('deletedCoreUsers');
          if (!deletedCoreUsers.includes(idStr)) {
            deletedCoreUsers.push(idStr);
            localStorage.setItem('deletedCoreUsers', JSON.stringify(deletedCoreUsers));
          }
        }

        const { error: userError } = await supabase.from('users').delete().eq('id', idStr);
        if (userError) throw userError;

        const { error: preRegError } = await supabase.from('pre_registrations').delete().eq('id', idStr);
        if (preRegError) throw preRegError;

        const { error: staffRegError } = await supabase.from('staff_registrations').delete().eq('id', idStr);
        if (staffRegError) throw staffRegError;

        return { success: true };
      } catch (error) {
        console.error('[Supabase] Error deleting user:', error);
        return { success: false, error: 'Erro ao excluir usuário e dados vinculados' };
      }
    },

    // Função de aprovação de staff
    approveStaffRegistration: async (discordId) => {
      try {
        const idStr = discordId.toString();
        const registration = staffRegistrations[idStr];
        if (!registration) return { success: false, error: 'Cadastro não encontrado' };

        // Calculate contract dates
        const startDate = new Date();
        // Calculate contract dates - Prefer data from the signed form (contractData), fallback to pre-registration
        const formDuration = registration.contractData?.contractDuration || registration.contractData?.contractMonths;
        const formUnit = registration.contractData?.contractUnit;

        const duration = parseInt(formDuration || registration.preStaffData.contractDuration) || 12; // Default 12
        const unit = formUnit || registration.preStaffData.contractUnit || 'months';

        const endDate = new Date(startDate);
        if (unit === 'days') {
          endDate.setDate(endDate.getDate() + duration);
        } else {
          endDate.setMonth(endDate.getMonth() + duration);
        }

        // 1. Create the payload for DB (Strictly filtered)
        // 1. Create the payload for DB (Snake Case)
        const userDbPayload = {
          id: idStr,
          discord_id: idStr,
          name: registration.personalData.fullName,
          email: registration.discordData.email,
          phone: registration.personalData.phone,
          avatar: registration.discordData.avatar,
          access_level: registration.preStaffData.accessLevel,
          registration_status: 'registered',
          is_staff: true,

          // New Root Columns
          terms: registration.terms,
          signature: {
            text: registration.signature,
            signedAt: new Date().toISOString()
          },

          staff_details: {
            personalData: registration.personalData,
            businessData: registration.businessData,
            bankingData: registration.bankingData,
            codename: registration.preStaffData.codename, // Preserve codename from pre-registration
            contractData: {
              ...registration.contractData,
              contractDuration: duration, // Ensure this is saved
              contractMonths: duration, // Legacy compatibility
              contractUnit: unit,
              startDate: startDate.toISOString(),
              endDate: endDate.toISOString(),
              status: 'active'
            }
          },
          created_at: new Date().toISOString()
        };

        const { error: userError } = await supabase.from('users').upsert(userDbPayload);
        if (userError) throw userError;

        const { error: preRegError } = await supabase.from('pre_registrations').delete().eq('id', idStr);
        if (preRegError) throw preRegError;

        const { error: staffRegError } = await supabase.from('staff_registrations').delete().eq('id', idStr);
        if (staffRegError) throw staffRegError;

        // Manually update local state for immediate UI feedback (before realtime sync)
        if (typeof setStaffRegistrations === 'function') {
          setStaffRegistrations(prev => {
            const newMap = { ...prev };
            delete newMap[idStr];
            return newMap;
          });
        }

        if (typeof setPreRegisteredStaff === 'function') {
          setPreRegisteredStaff(prev => {
            const newMap = { ...prev };
            delete newMap[idStr];
            return newMap;
          });
        }

        return { success: true };
      } catch (error) {
        console.error('[Supabase] Error approved staff:', error);
        return { success: false, error: 'Erro ao aprovar cadastro' };
      }
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Simulações de API (em produção, substituir por chamadas reais)

const getSavedUsers = () => {
  try {
    const parsed = safeParseObject('registeredUsers', null);

    // BACKWARD COMPATIBILITY / FIX: Se for um array, converte para objeto mapeado por discordId
    if (Array.isArray(parsed)) {
      console.warn('[Auth] registeredUsers detectado como array. Convertendo para objeto...');
      const obj = {};
      parsed.forEach(u => {
        if (u && u.discordId) {
          obj[u.discordId.toString()] = u;
        }
      });
      // Salva imediatamente a versão corrigida
      localStorage.setItem('registeredUsers', JSON.stringify(obj));
      return obj;
    }

    return parsed || {};
  } catch (error) {
    console.error('Erro ao carregar usuários salvos:', error);
    return {};
  }
};

// PREDEFINED / CORE USERS
const getCoreUsers = () => {
  const predefinedChanges = safeParseObject('predefinedUserChanges');

  const adminUsers = {
    /*
    '401033888303939584': {
      id: 1, discordId: '401033888303939584', name: 'Administrador', email: 'admin@grupocapital.com',
      phone: '(11) 99999-0000', accessLevel: predefinedChanges['401033888303939584']?.accessLevel || 'admin',
      cities: [
        { city: 'CAPITAL', playerId: 'ADM001', characterName: 'Admin_Capital' },
        { city: 'VALLEY NORTE', playerId: 'ADM002', characterName: 'Admin_Valley' },
        { city: 'VALLEY SUL', playerId: 'ADM003', characterName: 'Admin_Sul' },
        { city: 'MORADA DO VALLEY', playerId: 'ADM004', characterName: 'Admin_Morada' }
      ],
      allowedCities: predefinedChanges['401033888303939584']?.allowedCities || ['CAPITAL', 'VALLEY NORTE', 'VALLEY SUL', 'MORADA DO VALLEY'],
      createdAt: '2024-01-01', password: '22051997@', registrationStatus: 'registered'
    },
    */
    '862470916239785984': {
      id: 2, discordId: '862470916239785984', name: 'Admin 2', email: 'admin2@grupocapital.com',
      phone: '(11) 99999-0002', accessLevel: predefinedChanges['862470916239785984']?.accessLevel || 'admin',
      cities: [{ city: 'CAPITAL', playerId: 'ADM010', characterName: 'Admin_Principal' }],
      allowedCities: predefinedChanges['862470916239785984']?.allowedCities || ['CAPITAL', 'VALLEY NORTE', 'VALLEY SUL', 'MORADA DO VALLEY'],
      createdAt: '2024-01-01', password: 'admin', registrationStatus: 'registered'
    },
    '1217325550771765291': {
      id: 3, discordId: '1217325550771765291', name: 'Administrador 2', email: 'admin2@grupocapital.com',
      phone: '(11) 99999-0003', accessLevel: predefinedChanges['1217325550771765291']?.accessLevel || 'admin',
      cities: [{ city: 'VALLEY SUL', playerId: 'ADM020', characterName: 'Admin_Secundario' }],
      allowedCities: predefinedChanges['1217325550771765291']?.allowedCities || ['CAPITAL', 'VALLEY NORTE', 'VALLEY SUL', 'MORADA DO VALLEY'],
      createdAt: '2024-01-01', password: 'admin', registrationStatus: 'registered'
    }
  };

  const testStaff = {
    'test_terminating_user': {
      id: 99, discordId: 'test_terminating_user', name: 'Prestador em Encerramento', email: 'terminando@grupocapital.com',
      phone: '(11) 99999-9999', accessLevel: 'suporte', registrationStatus: 'registered',
      isStaff: true,
      staffDetails: {
        contractSigned: true,
        contractData: {
          contractValue: 5000,
          contractDuration: 3,
          contractUnit: 'months',
          contractTerm: '3 meses',
          serviceDescription: 'Suporte Finalizando',
          jobFunction: 'Suporte',
          status: 'active',
          startDate: '2025-10-08T00:00:00.000Z',
          endDate: '2026-01-08T00:00:00.000Z' // 1 day from now
        },
        personalData: { fullName: 'Prestador Teste Final', cpf: '000.000.000-00', phone: '(11) 99999-9999' },
        businessData: { businessName: 'Test Services LTDA', cnpj: '00.000.000/0001-00', fullAddress: 'Rua Teste, 123' },
        bankingData: { bank: 'Banco Teste', account: '123456', agency: '0001', pixKey: 'test@pix.com', pixKeyType: 'email' }
      },
      cities: [{ city: 'CAPITAL', playerId: 'TERM001', characterName: 'Terminando_Char' }],
      allowedCities: ['CAPITAL'], createdAt: '2024-01-01', password: '123456'
    },
    'test_suporte': {
      id: 10, discordId: 'test_suporte', name: 'João Suporte', email: 'suporte@grupocapital.com', phone: '(11) 99999-0010',
      accessLevel: 'suporte', registrationStatus: 'registered',
      cities: [{ city: 'CAPITAL', playerId: 'SUP001', characterName: 'Joao_Suporte' }],
      allowedCities: ['CAPITAL', 'VALLEY NORTE'], createdAt: '2024-01-01', password: '123456'
    },
    'test_suporte_compras': {
      id: 11, discordId: 'test_suporte_compras', name: 'Maria Compras', email: 'compras@grupocapital.com', phone: '(11) 99999-0011',
      accessLevel: 'suporte_compras', registrationStatus: 'registered',
      cities: [{ city: 'CAPITAL', playerId: 'COM001', characterName: 'Maria_Compras' }],
      allowedCities: ['CAPITAL', 'VALLEY SUL'], createdAt: '2024-01-01', password: '123456'
    },
    'test_suporte_descontos': {
      id: 12, discordId: 'test_suporte_descontos', name: 'Pedro Descontos', email: 'descontos@grupocapital.com', phone: '(11) 99999-0012',
      accessLevel: 'suporte_descontos', registrationStatus: 'registered',
      cities: [{ city: 'CAPITAL', playerId: 'DESC001', characterName: 'Pedro_Descontos' }],
      allowedCities: ['VALLEY NORTE', 'VALLEY SUL', 'MORADA DO VALLEY'], createdAt: '2024-01-01', password: '123456'
    },
    'test_moderador': {
      id: 13, discordId: 'test_moderador', name: 'Ana Moderadora', email: 'moderador@grupocapital.com', phone: '(11) 99999-0013',
      accessLevel: 'moderador', registrationStatus: 'registered',
      cities: [{ city: 'CAPITAL', playerId: 'MOD001', characterName: 'Ana_Moderadora' }],
      allowedCities: ['CAPITAL'], createdAt: '2024-01-01', password: '123456'
    },
    'test_super_moderador': {
      id: 14, discordId: 'test_super_moderador', name: 'Carlos Super Mod', email: 'supermod@grupocapital.com', phone: '(11) 99999-0014',
      accessLevel: 'super_moderador', registrationStatus: 'registered',
      cities: [{ city: 'CAPITAL', playerId: 'SMOD001', characterName: 'Carlos_SuperMod' }],
      allowedCities: ['CAPITAL', 'VALLEY NORTE'], createdAt: '2024-01-01', password: '123456'
    },
    'test_programador': {
      id: 15, discordId: 'test_programador', name: 'Lucas Programador', email: 'dev@grupocapital.com', phone: '(11) 99999-0015',
      accessLevel: 'programador', registrationStatus: 'registered',
      cities: [{ city: 'CAPITAL', playerId: 'DEV001', characterName: 'Lucas_Dev' }],
      allowedCities: ['CAPITAL', 'VALLEY NORTE', 'VALLEY SUL', 'MORADA DO VALLEY'], createdAt: '2024-01-01', password: '123456'
    },
    'test_head_suporte': {
      id: 20, discordId: 'test_head_suporte', name: 'Fernanda Head Suporte', email: 'headsuporte@grupocapital.com', phone: '(11) 99999-0020',
      accessLevel: 'head_suporte', registrationStatus: 'registered',
      cities: [{ city: 'CAPITAL', playerId: 'HSUP001', characterName: 'Fernanda_HeadSup' }],
      allowedCities: ['CAPITAL', 'VALLEY NORTE', 'VALLEY SUL'], createdAt: '2024-01-01', password: '123456'
    },
    'test_head_moderacao': {
      id: 21, discordId: 'test_head_moderacao', name: 'Roberto Head Moderação', email: 'headmod@grupocapital.com', phone: '(11) 99999-0021',
      accessLevel: 'head_moderacao', registrationStatus: 'registered',
      cities: [{ city: 'CAPITAL', playerId: 'HMOD001', characterName: 'Roberto_HeadMod' }],
      allowedCities: ['CAPITAL', 'VALLEY NORTE'], createdAt: '2024-01-01', password: '123456'
    },
    'test_head_staff': {
      id: 22, discordId: 'test_head_staff', name: 'Juliana Head Staff', email: 'headstaff@grupocapital.com', phone: '(11) 99999-0022',
      accessLevel: 'head_staff', registrationStatus: 'registered',
      cities: [{ city: 'CAPITAL', playerId: 'HSTF001', characterName: 'Juliana_HeadStaff' }],
      allowedCities: ['CAPITAL', 'VALLEY NORTE', 'VALLEY SUL', 'MORADA DO VALLEY'], createdAt: '2024-01-01', password: '123456'
    }
  };

  return { ...testStaff, ...adminUsers };
};

const calculateAllUsers = () => {
  try {
    const saved = getSavedUsers();
    const core = getCoreUsers();

    // Load deleted core users blacklist
    const deletedCoreUsers = safeParseArray('deletedCoreUsers');

    // Mergear mantendo dados de logins/activity dos salvos, mas sobrepondo info básica do core
    // Garantimos que merged comece como um objeto limpo para evitar problemas com arrays residuais
    const merged = { ...saved };

    Object.keys(core).forEach(id => {
      const stringId = id.toString();

      // Skip if this core user was explicitly deleted
      if (deletedCoreUsers.includes(stringId)) {
        return;
      }

      merged[stringId] = {
        ...core[id],
        ...(saved[stringId] || {})
      };
    });

    return merged;
  } catch (error) {
    console.error('[Auth] Erro ao calcular mapa global de usuários:', error);
    return getCoreUsers(); // Fallback para usuários do sistema se tudo falhar
  }
};

// Função para salvar usuários
const saveUsers = (users) => {
  try {
    localStorage.setItem('registeredUsers', JSON.stringify(users));
  } catch (error) {
    console.error('Erro ao salvar usuários:', error);
  }
};

// NOVA// Simulações de API (em produção, substituir por chamadas reais)
const simulateLogin = async (discordId, password) => {
  await new Promise(resolve => setTimeout(resolve, 1000));

  const allUsersMap = calculateAllUsers();
  const user = allUsersMap[discordId.toString()];

  if (user && user.password === password) {
    // Update last login
    user.lastLogin = new Date().toISOString();
    const allUsers = getSavedUsers();
    if (allUsers[discordId]) {
      allUsers[discordId].lastLogin = user.lastLogin;
      saveUsers(allUsers);
    }
    return { success: true, user: user };
  } else if (user) {
    return { success: false, error: 'Senha incorreta' };
  } else {
    return { success: false, error: 'Usuário não encontrado' };
  }
};

// Simula verificação de existência do usuário
const simulateUserCheck = async (discordId) => {
  await new Promise(resolve => setTimeout(resolve, 500));

  if (!discordId) return { exists: false, discordId };

  const allUsersMap = calculateAllUsers();
  const exists = !!allUsersMap[discordId.toString()];

  console.log(`[Auth] Verificando existência de ${discordId}: ${exists}`);

  return {
    exists,
    discordId: discordId.toString()
  };
};

// Simula registro de usuário
const simulateRegister = async (userData) => {
  console.log('=== INÍCIO DO REGISTRO ===');
  console.log('Dados recebidos:', userData);

  await new Promise(resolve => setTimeout(resolve, 1500));

  // Simula validações básicas
  if (!userData.discordId || !userData.name || !userData.email) {
    console.log('Erro: Campos obrigatórios faltando');
    return { success: false, error: 'Todos os campos obrigatórios devem ser preenchidos' };
  }

  try {
    console.log('Iniciando processo de salvamento...');

    // Verifica se usuário já existe
    const savedUsers = getSavedUsers();
    const idToRegister = userData.discordId.toString();

    console.log('Verificando se ID já existe:', idToRegister);

    if (savedUsers[idToRegister]) {
      console.log('Erro: Usuário já existe no banco de dados');
      return { success: false, error: 'Usuário já existe' };
    }

    // Cria novo usuário
    const newUser = {
      id: Date.now(), // ID simples baseado em timestamp
      discordId: userData.discordId,
      name: userData.name,
      email: userData.email,
      phone: userData.phone,
      avatar: userData.avatar, // Novo campo
      language: userData.language || 'pt-BR', // Novo campo
      accessLevel: 'user', // Novos usuários sempre começam como 'user'
      cities: userData.cities || [], // Array de cidades extraídas
      joinDate: userData.joinDate, // Data de entrada no discord
      isBooster: userData.isBooster || false, // Se é booster
      ip: generateMockIP(), // MOCK: IP Detection Logic
      registrationStatus: 'registered',
      createdat: new Date().toISOString().split('T')[0],

      // ========== FROZEN DATA (NEVER CHANGES) ==========
      frozenData: {
        servidor: userData.cities && userData.cities.length > 0
          ? userData.cities[0].city
          : null,

        playerId: userData.cities && userData.cities.length > 0
          ? userData.cities[0].playerId
          : null,

        initialNickname: userData.cities && userData.cities.length > 0
          ? userData.cities[0].characterName
          : userData.name,

        initialRoles: userData.cities && userData.cities.length > 0
          ? userData.cities[0].roles || []
          : [],

        frozenAt: new Date().toISOString(),
        frozenBy: 'system_registration'
      },

      dataVersion: 'v1.0.0'
    };

    console.log('Novo usuário criado:', newUser);

    // Initialize history for new user
    initializeHistory(userData.discordId, {
      servidor: newUser.frozenData.servidor,
      playerId: newUser.frozenData.playerId,
      nickname: newUser.frozenData.initialNickname,
      email: newUser.email,
      name: newUser.name
    });

    // Salva o usuário
    const updatedUsers = {
      ...savedUsers,
      [userData.discordId]: newUser
    };

    console.log('Salvando usuários atualizados:', updatedUsers);
    saveUsers(updatedUsers);

    // Verificar se foi salvo
    const verification = getSavedUsers();
    console.log('Verificação após salvamento:', verification);

    console.log('Usuário cadastrado com sucesso:', {
      discordId: newUser.discordId,
      name: newUser.name,
      email: newUser.email
    });

    return { success: true, user: newUser };
  } catch (error) {
    console.error('Erro ao salvar usuário:', error);
    return { success: false, error: 'Erro interno do servidor' };
  }
};


const getUserById = async (discordId) => {
  if (!discordId) return null;
  await new Promise(resolve => setTimeout(resolve, 500));
  const allUsersMap = calculateAllUsers();
  return allUsersMap[discordId.toString()] || null;
};
